"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { PanelLeftOpen } from "lucide-react";
import { PrimaryNav } from "./primary-nav";
import { TenantLocationSelector } from "./tenant-location-selector";
import { CommandSearch } from "./command-search";
import { NotificationCenter } from "./notification-center";
import { UserMenu } from "./user-menu";
import { ContextBar, type ContextBarProps } from "./context-bar";
import { MOCK_LOCATIONS } from "@/lib/mock-data";
import { DensityToggle } from "@/components/controls/density-toggle";
import { ThemeToggle } from "@/components/controls/theme-toggle";
import { DirectionToggle } from "@/components/controls/direction-toggle";

export interface GlobalShellProps {
  activeNavId: string;
  onNavigate?: (id: string) => void;
  contextBar: Omit<ContextBarProps, "location"> & { locationId?: string };
  children: ReactNode;
}

/**
 * Global Shell — tenant/location selector, primary navigation, global
 * search, notification center and current user/role (docs/COMPONENT_LIBRARY.md).
 * Composed here with the Context Bar into one real page shell, not just
 * gallery fragments, per the Phase 0 brief.
 */
export function GlobalShell({ activeNavId, onNavigate, contextBar, children }: GlobalShellProps) {
  const [locationId, setLocationId] = useState(contextBar.locationId ?? MOCK_LOCATIONS[0].id);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = MOCK_LOCATIONS.find((loc) => loc.id === locationId) ?? MOCK_LOCATIONS[0];

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-[260px_1fr]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:start-2 focus:top-2 focus:z-50 focus:rounded-control focus:bg-brand-primary focus:px-3 focus:py-2 focus:text-sm focus:font-bold focus:text-brand-on"
      >
        Skip to main content
      </a>

      <aside
        className={`border-e border-border-subtle bg-surface-primary p-4 md:sticky md:top-0 md:h-screen md:overflow-y-auto ${
          mobileNavOpen ? "block" : "hidden md:block"
        }`}
        aria-label="Sidebar"
      >
        <Link href="/" className="mb-4 flex items-center gap-3 rounded-control px-1 py-1">
          <span className="flex h-9 w-9 items-center justify-center rounded-control bg-[linear-gradient(135deg,var(--color-brand-primary),var(--color-brand-deep))] font-display text-base font-medium text-white">
            H
          </span>
          <span>
            <span className="block text-sm font-bold uppercase tracking-wide text-text-primary">Highland Host</span>
            <span className="block text-[10px] text-text-secondary">Guest &amp; Revenue Platform</span>
          </span>
        </Link>

        <TenantLocationSelector locations={MOCK_LOCATIONS} value={locationId} onChange={setLocationId} className="mb-3" />

        <PrimaryNav activeId={activeNavId} onSelect={(id) => onNavigate?.(id)} />

        <div className="mt-6 space-y-2 border-t border-border-subtle pt-4">
          <p className="px-1 text-[10px] font-bold uppercase tracking-wider text-text-secondary">Display</p>
          <DensityToggle className="w-full" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <DirectionToggle />
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-border-subtle bg-surface-primary/80 px-4 py-3 backdrop-blur sm:px-6">
          <button
            type="button"
            onClick={() => setMobileNavOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-control border border-border-subtle text-text-secondary md:hidden"
            aria-label="Toggle navigation"
            aria-expanded={mobileNavOpen}
          >
            <PanelLeftOpen aria-hidden="true" size={16} />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <CommandSearch />
            <NotificationCenter />
            <UserMenu />
          </div>
        </header>

        <ContextBar
          location={location}
          service={contextBar.service}
          dateLabel={contextBar.dateLabel}
          operatingState={contextBar.operatingState}
        />

        <main
          id="main-content"
          className="flex-1 p-4 sm:p-6"
          style={{ display: "flex", flexDirection: "column", gap: "var(--density-block-gap)" }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
